import { createSelector, type ParametricSelector, type Selector } from "reselect";

import { areObjectsDeepEqual } from "@ppb/tbd-store/helpers/obj-deep-equal-comparator";
import { NavigationTabsTitle } from "@ppb/tbd-store/middlewares/tagging-resolvers/AnalyticsConstants";
import type { ApplicationState } from "@ppb/tbd-store/state";
import { createNavigationTabsListByURNSelector } from "@ppb/tbd-store/state/layout/navigation-tabs-list/navigation-tabs-list-selectors";
import { createNavigationTabByURNSelector } from "@ppb/tbd-store/state/layout/navigation-tabs/navigation-tabs-selectors";
import type {
  NavigationTabListContent,
  NavigationTabListHeader,
  NavigationTabListItemPartial,
  NavigationTabListProcessed,
} from "@ppb/tbd-store/state/layout/navigation-tabs-list/NavigationTabsList.types";
import type URN from "@ppb/tbd-store/state/layout/URN";
import { IconsList } from "@ppb/the-wall-icons/GenericIcon/GenericIcon";

import { buildTranslatableText } from "../helpers/translatable-text";

export type NavigationTabsLayout = Omit<NavigationTabListProcessed, "urn" | "typename">;

const EMPTY_ITEMS: NavigationTabListContent["items"] = [];

const isFavouritesTab = (item: NavigationTabListItemPartial): boolean =>
  item.typename === "FavouriteMarketsNavigationTab";

const createNavigationTabsListContentsByURNSelector = (): ParametricSelector<
  ApplicationState,
  NavigationTabListItemPartial[],
  NavigationTabListContent[]
> => {
  const getNavigationTab = createNavigationTabByURNSelector();

  return createSelector(
    [
      (state: ApplicationState) => !!state.entities.brandSettings?.ERROR_VIEW_IMAGE,
      (state: ApplicationState) => state.layouts.navigationtabs,
      (_, items: NavigationTabListItemPartial[]): NavigationTabListItemPartial[] => items,
    ],
    (hasEmptyStateImage, navigationtabs, items): NavigationTabListContent[] => {
      return items.map((item): NavigationTabListContent => {
        const navigationTab = getNavigationTab(navigationtabs, item.urn);

        return {
          id: item.urn,
          items: navigationTab?.items || EMPTY_ITEMS,
          hasContent: navigationTab?.items ? navigationTab.items.length > 0 : true,
          isFavouriteMarketsTab: isFavouritesTab(item),
          hasEmptyStateImage: hasEmptyStateImage,
        };
      });
    },
    // Recomputes whenever any tab in `layouts.navigationtabs` changes, not only this list's tabs.
    { memoizeOptions: { resultEqualityCheck: areObjectsDeepEqual } },
  );
};

const createNavigationTabsListHeadersByURNSelector = (): Selector<
  NavigationTabListItemPartial[],
  NavigationTabListHeader[]
> =>
  createSelector([(items): NavigationTabListItemPartial[] => items], (items): NavigationTabListHeader[] =>
    items.map((item): NavigationTabListHeader => {
      const commonHeaderProps = {
        id: item.urn,
        title: buildTranslatableText(item.title) || "",
        statusLabelText: item.badgeText ? buildTranslatableText(item.badgeText) : undefined,
        viewLink: item.viewLink,
      };

      if (isFavouritesTab(item)) {
        return {
          ...commonHeaderProps,
          ariaLabel: NavigationTabsTitle.FAVOURITE_MARKETS,
          icon: IconsList.STAR_FILLED,
        };
      }

      return commonHeaderProps;
    }),
  );

/**
 * createNavigationTabsLayoutByURNSelector
 * For a given view URN, returns the corresponding navigationTabsLayout with the corresponding NavigationTab items
 */
export const createNavigationTabsLayoutByURNSelector = (): ParametricSelector<
  ApplicationState,
  { urn: URN; localeCode?: string },
  NavigationTabsLayout | undefined
> => {
  const getNavigationTabsList = createNavigationTabsListByURNSelector();
  const getNavigationTabsListHeaders = createNavigationTabsListHeadersByURNSelector();
  const getNavigationTabsListContents = createNavigationTabsListContentsByURNSelector();

  return createSelector(
    [
      (state: ApplicationState): ApplicationState => state,
      (_: ApplicationState, { urn }: { urn: URN; localeCode?: string }): URN => urn,
      /* The localeCode is required to handle language setting change on native application - this needs to trigger the
       ** translatableTexts, but we don't need localeCode value because i18n library is already updated with it */
      (_: ApplicationState, { localeCode }: { urn: URN; localeCode?: string }): string | undefined => localeCode,
    ],
    (state, urn): NavigationTabsLayout | undefined => {
      const { layouts } = state;
      const navigationTabsList = getNavigationTabsList(layouts.navigationtabslists, urn);

      if (!navigationTabsList?.items.length) {
        return undefined;
      }

      return {
        title: navigationTabsList.title,
        headers: getNavigationTabsListHeaders(navigationTabsList.items),
        contents: getNavigationTabsListContents(state, navigationTabsList.items),
        selectedTabUrn: navigationTabsList.selectedTabUrn,
      };
    },
  );
};
